// Text-to-Speech Service for playing audio files
// This service manages audio playback for assistant messages

export interface TextToSpeechOptions {
  lang: 'en' | 'nl';
  enabled: boolean;
}

class TextToSpeechService {
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private isPlaying: boolean = false;
  private queue: string[] = [];
  private currentAudio: HTMLAudioElement | null = null;

  // Global state for text-to-speech
  private static _textToSpeechEnabled: boolean = false;

  // Getter for global state
  static get textToSpeechEnabled(): boolean {
    return TextToSpeechService._textToSpeechEnabled;
  }

  // Setter for global state
  static set textToSpeechEnabled(value: boolean) {
    TextToSpeechService._textToSpeechEnabled = value;
  }

  // Global state for language
  private static _currentLanguage: 'en' | 'nl' = 'en';

  // Getter for global language
  static get currentLanguage(): 'en' | 'nl' {
    return TextToSpeechService._currentLanguage;
  }

  // Setter for global language
  static set currentLanguage(value: 'en' | 'nl') {
    TextToSpeechService._currentLanguage = value;
  }

  /**
   * Play audio for a specific i18n key
   */
  async playAudio(key: string, options?: TextToSpeechOptions): Promise<void> {
    // Use global state if no options provided
    const enabled = options?.enabled ?? TextToSpeechService.textToSpeechEnabled;
    const lang = options?.lang ?? TextToSpeechService.currentLanguage;
    
    // Only play if enabled and language is Dutch
    if (!enabled || lang !== 'nl') {
      return;
    }

    // Add to queue
    this.queue.push(key);
    
    // If nothing is currently playing, start playing
    if (!this.isPlaying) {
      this.processQueue();
    }
  }

  /**
   * Process the audio queue
   */
  private async processQueue(): Promise<void> {
    if (this.queue.length === 0) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    const key = this.queue.shift()!;
    
    try {
      await this.playSingleAudio(key);
    } catch (error) {
      console.error(`Error playing audio for key ${key}:`, error);
      // Continue with next audio in queue
      this.processQueue();
    }
  }



  /**
   * Play a single audio file
   */
  private async playSingleAudio(key: string): Promise<void> {
    // Check if audio is cached
    let audio = this.audioCache.get(key);
    
    if (!audio) {
      // Try MP3 first, then M4A as fallback - simple approach without HEAD requests
      audio = new Audio(`/audio/audio-i18n/${key}.mp3`);
      
      // If MP3 fails to load, try M4A
      audio.onerror = () => {
        console.log(`MP3 failed for ${key}, trying M4A...`);
        if (audio) {
          audio.src = `/audio/audio-i18n/${key}.m4a`;
        }
      };
      
      audio.preload = 'auto';
      
      // Cache the audio element
      this.audioCache.set(key, audio);
    }

    this.currentAudio = audio;

    // Wait for audio to finish
    await new Promise<void>((resolve, reject) => {
      audio!.onended = () => {
        this.currentAudio = null;
        resolve();
      };
      
      audio!.onerror = (error) => {
        console.error(`Audio error for key ${key}:`, error);
        this.currentAudio = null;
        reject(error);
      };

      // Play the audio
      audio!.play().catch(reject);
    });

    // Process next audio in queue
    this.processQueue();
  }

  /**
   * Stop all audio playback
   */
  stopAll(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    
    this.queue = [];
    this.isPlaying = false;
  }

  /**
   * Clear audio cache
   */
  clearCache(): void {
    this.audioCache.clear();
  }

  /**
   * Check if audio is currently playing
   */
  getPlayingStatus(): boolean {
    return this.isPlaying;
  }

  /**
   * Get current queue length
   */
  getQueueLength(): number {
    return this.queue.length;
  }
}

// Export singleton instance
export const textToSpeechService = new TextToSpeechService();

// Export the class for testing purposes
export { TextToSpeechService };
